/**
 * The daily weigh-in: one day's record of the body, per `/specs/features/weighin.spec.md`.
 * Girths travel in metres like every stored length; centimetres are a display concern. Body
 * fat travels as the ratio it is, and the per cent is the same kind of display concern.
 */

/** The fixed list, in the order the form offers it. */
export const MEASUREMENTS = [
  'neck',
  'chest',
  'waist',
  'hips',
  'biceps',
  'thigh',
  'calf',
  'bodyFat',
  // How you slept, as the two clock times it is known by, kept as minutes past midnight.
  'bedtime',
  'wake',
] as const

export type Measurement = (typeof MEASUREMENTS)[number]

export const MEASUREMENT_LABELS: Record<Measurement, string> = {
  neck: 'Neck',
  chest: 'Chest',
  waist: 'Waist',
  hips: 'Hips',
  biceps: 'Biceps',
  thigh: 'Thigh',
  calf: 'Calf',
  bodyFat: 'Body fat',
  bedtime: 'To bed',
  wake: 'Woke up',
}

/**
 * What a measurement is read and entered in. Everything on the tape is a length in centimetres;
 * body fat is the one that is not, and it says so here rather than at each field.
 */
export const MEASUREMENT_UNITS: Record<Measurement, string> = {
  neck: 'cm',
  chest: 'cm',
  waist: 'cm',
  hips: 'cm',
  biceps: 'cm',
  thigh: 'cm',
  calf: 'cm',
  bodyFat: '%',
  // A time of day is written as a clock, which says what it is without a unit after it.
  bedtime: '',
  wake: '',
}

/**
 * A measurement as it is read, from what it is kept as. A girth is metres to centimetres and
 * body fat is a ratio to a per cent, which is the same hundredfold — so one rounding serves
 * both, to a tenth of whatever it is read in.
 */
export function measurementDisplay(value: number): number {
  return Math.round(value * 1000) / 10
}

/** The other way: what was typed, as it is kept. */
export function measurementStored(display: number): number {
  return display / 100
}

/**
 * The two readings that are a time of day rather than a number. They are asked for together
 * and chosen together: a bedtime with no wake time is half a night, so "Sleep" is one thing a
 * day logs and these are its two halves.
 */
export const SLEEP_MEASUREMENTS = ['bedtime', 'wake'] as const

/**
 * The one name sleep is chosen by. Nothing is kept under it — a day logs it or does not as one
 * thing, so this is what the choice is called and `SLEEP_MEASUREMENTS` is what choosing it
 * turns on.
 */
export const SLEEP_CHOICE = 'bedtime'

/** What a day can be set to log, with sleep standing for its two halves. */
export const ROUTINE_CHOICES = [
  ...MEASUREMENTS.filter((name) => !(SLEEP_MEASUREMENTS as readonly string[]).includes(name)),
  SLEEP_CHOICE,
] as const

/**
 * How long a night came to, in minutes, from the two times it is known by. Null until both are
 * in: a bedtime with no wake time is half a night, and half a night is no length at all.
 *
 * The bedtime is last night's and the wake time is this morning's, so the clock has usually
 * gone round midnight between them — which is why the wake time being the smaller number is
 * the ordinary case rather than a mistake.
 */
export function sleepMinutes(taken: Record<Measurement, number | null>): number | null {
  const bed = taken.bedtime
  const woke = taken.wake
  if (bed == null || woke == null) {
    return null
  }
  return (woke - bed + 24 * 60) % (24 * 60)
}

/** A length of sleep as it is read: "7h 47m", and "7h" on the hour. */
export function sleepSaid(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  return rest === 0 ? `${String(hours)}h` : `${String(hours)}h ${String(rest)}m`
}

/** A minute of the day as a clock, which is how a time of day is written. */
export function clockSaid(minutes: number): string {
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(Math.floor(minutes / 60) % 24)}:${pad(minutes % 60)}`
}

/** The other way: a clock read back as the minute of the day it names, or null where it is not one. */
export function minutesOfClock(clock: string): number | null {
  const [hours, minutes] = clock.split(':').map(Number)
  if (hours === undefined || minutes === undefined) {
    return null
  }
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }
  return hours * 60 + minutes
}

/** The photo types a weigh-in knows by name; anything else is custom, wearing its label. */
export const PHOTO_TYPES = ['front', 'back', 'side', 'belly', 'legs', 'arms'] as const

export type PhotoType = (typeof PHOTO_TYPES)[number] | 'custom'

export const PHOTO_TYPE_LABELS: Record<(typeof PHOTO_TYPES)[number], string> = {
  front: 'Front',
  back: 'Back',
  side: 'Side',
  belly: 'Belly',
  legs: 'Legs',
  arms: 'Arms',
}

export interface WeighInPhoto {
  readonly id: string
  readonly day: string
  readonly type: PhotoType
  readonly label: string | null
  readonly hasPhoto: boolean
  readonly createdAt: number
}

export interface WeighIn {
  /** The local calendar day this belongs to, as `YYYY-MM-DD`. */
  readonly day: string
  readonly weightKg: number | null
  readonly measurements: Record<Measurement, number | null>
  readonly photos: WeighInPhoto[]
  readonly createdAt: number
  /** When it was last written, as an ISO-8601 string with milliseconds. */
  readonly updatedAt: string
}

export const EMPTY_MEASUREMENTS: Record<Measurement, number | null> = {
  neck: null,
  chest: null,
  waist: null,
  hips: null,
  biceps: null,
  thigh: null,
  calf: null,
  bodyFat: null,
  bedtime: null,
  wake: null,
}

/** Today where the person is — a weigh-in belongs to the morning it was made. */
export function todayKey(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${String(now.getFullYear())}-${month}-${day}`
}

/** What a photo is called where it is shown: its type's name, or its own label. */
export function photoName(photo: Pick<WeighInPhoto, 'type' | 'label'>): string {
  if (photo.type === 'custom') {
    return photo.label ?? 'Photo'
  }
  return PHOTO_TYPE_LABELS[photo.type]
}

/**
 * What the account's weigh-in row says: what the day already holds, or the invitation to hold
 * something. A weigh-in is the part somebody is looking for, so it leads; the photos are
 * counted after it.
 */
export function weighInEntry(weight: string | null, photos: number): string {
  const parts = [
    ...(weight === null ? [] : [weight]),
    ...(photos > 0 ? [`${String(photos)} ${photos === 1 ? 'photo' : 'photos'}`] : []),
  ]
  return parts.length === 0 ? 'Weigh-in' : `Weigh-in: ${parts.join(' · ')}`
}

/**
 * Whether a weigh-in holds anything at all. A day whose weight was taken back off it keeps its
 * row, and a row saying nothing is not a weigh-in anybody made — it must not stand on a
 * heading, and it must not shadow the real one behind it.
 */
export function weighInSaysSomething(weighIn: WeighIn): boolean {
  return (
    weighIn.weightKg != null ||
    MEASUREMENTS.some((name) => weighIn.measurements[name] != null) ||
    weighIn.photos.length > 0
  )
}

/**
 * The weigh-in that speaks for a day: that day's own, or the most recent one before it. Null
 * where nothing has been logged on or before the day at all — a row holding nothing counts as
 * nothing, so the last day that actually said something is what speaks.
 *
 * Days are ISO strings, so the latest one that is not after `day` is the nearest one behind it.
 */
/**
 * Whether a day has everything the account has set it to log — the weigh-in spec, The nudge.
 *
 * The weight and every measurement chosen in what a day logs. Somebody who logs a weight and a
 * waist has not finished their morning with the weight alone, and a nudge that went at the
 * weight was telling them they were done when they were not. A measurement that was never
 * chosen is not missing, whatever the day happens to hold of it.
 */
/**
 * Whether the morning the nudge is about has begun — the weigh-in spec, The nudge.
 *
 * It used to stand from midnight, which is a note about a weigh-in shown to somebody who has
 * not been to bed yet. It waits for the hour the account set instead, seven in the morning
 * until somebody says otherwise.
 */
export function nudgeHourReached(from: number, now: Date = new Date()): boolean {
  return now.getHours() * 60 + now.getMinutes() >= from
}

export function weighInComplete(taken: WeighIn | null, routine: readonly string[]): boolean {
  if (taken?.weightKg == null) {
    return false
  }
  return MEASUREMENTS.filter((name) => routine.includes(name)).every(
    (name) => taken.measurements[name] != null,
  )
}

export function weighInFor(day: string, weighIns: readonly WeighIn[]): WeighIn | null {
  const behind = weighIns.filter((weighIn) => weighIn.day <= day && weighInSaysSomething(weighIn))
  if (behind.length === 0) return null
  return behind.reduce((nearest, weighIn) => (weighIn.day > nearest.day ? weighIn : nearest))
}

/**
 * How long before `day` a weigh-in was taken, in words, or null when it is that day's own.
 *
 * A number from another day is not the same claim as a number from this one, so a recap that
 * borrowed one and said nothing would be quietly wrong about the body it describes.
 */
export function weighInAge(weighInDay: string, day: string): string | null {
  const days = Math.round((Date.parse(day) - Date.parse(weighInDay)) / 86_400_000)
  if (days <= 0) return null
  return days === 1 ? 'the day before' : `${String(days)} days before`
}
